import {Injectable} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {DyeingMachineType} from '@domain/machine/dyeing-machine-type';
import {MachineType} from '@domain/machine/machine-type.enum';
import {PlasticsMachineType} from '@domain/machine/plastics-machine-type';
import {TuftingMachineType} from '@domain/machine/tufting-machine-type';
import {WeavingMachineType} from '@domain/machine/weaving-machine-type';
import {HttpMachineTypesService} from '@infrastructure/http/machine-type/http-machine-types.service';
import {MachineTypeDetail} from '@presentation/pages/machine-overview/add-equipment-kind/generic-equipment-kind-form/select-machine-type/machine-type-detail.interface';
import {ColDefBuilderFactoryService, StringUtils, TranslateService} from '@vdw/angular-component-library';
import {ColDef, ValueFormatterParams, ValueGetterParams} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {GenericMachineType} from '../machine-form';

@Injectable({
  providedIn: 'root'
})
export class SelectMachineTypeService {
  private readonly translationKeyMap = new Map<string, string>([
    ['centimeter', 'GENERAL.UNIT.CENTIMETER'],
    ['inch', 'GENERAL.UNIT.INCH'],
    ['gauge', 'GENERAL.DIMENSIONS.GAUGE'],
    ['manufacturer', 'MACHINE.MANUFACTURER'],
    ['model', 'MACHINE.MODEL'],
    ['technology', 'MACHINE.TECHNOLOGY'],
    ['technologies', 'MACHINE.TECHNOLOGIES'],
    ['width', 'GENERAL.DIMENSIONS.WIDTH'],
    ['controllerType', 'MACHINE.CONTROLLER_TYPE'],
    ['kg', 'GENERAL.UNIT.KILOGRAM'],
    ['minimumLoad', 'MACHINE.MINIMUM_LOAD'],
    ['maximumLoad', 'MACHINE.MAXIMUM_LOAD'],
    ['celsius', 'GENERAL.UNIT.CELSIUS'],
    ['maximumTemperature', 'MACHINE.MAXIMUM_TEMPERATURE'],
    ['standardSpeed', 'MACHINE.DETAILS.STANDARD_SPEED'],
    ['standardSpeeds', 'MACHINE.DETAILS.STANDARD_SPEEDS']
  ]);

  public constructor(
    private readonly machineTypes: HttpMachineTypesService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translateService: TranslateService
  ) {}

  public getColumnDefsForMachineType(equipmentType: MachineType): ColDef[] {
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return this.getColumnDefsForWeavingMachine();
      case MachineType.TUFT:
        return this.getColumnDefsForTuftingMachine();
      case MachineType.WINDER:
        return this.getColumnDefsForWinderMachine();
      case MachineType.DYEING:
        return this.getColumnDefsForDyeingMachine();
      case MachineType.PLASTIC_MACHINE:
        return this.getColumnDefsForPlasticsMachine();
    }
  }

  public getAvailableTypes(equipmentType: MachineType): Observable<any[]> {
    return this.machineTypes.getAll(equipmentType);
  }

  public canSelectMachineType(machineType: MachineType): boolean {
    return [
      MachineType[MachineType.TUFT],
      MachineType[MachineType.WEAVING_MACHINE],
      MachineType[MachineType.WINDER],
      MachineType[MachineType.DYEING],
      MachineType[MachineType.PLASTIC_MACHINE]
    ].includes(MachineType[machineType]);
  }

  public getBackendRoute(equipmentType: MachineType): string {
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return 'weaving';
      case MachineType.TUFT:
        return 'tufting';
      case MachineType.WINDER:
        return 'winder';
      case MachineType.DYEING:
        return 'dyeing';
    }
  }

  public getTranslationKey(equipmentType: MachineType): string {
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return 'MACHINE.DETAILS.MACHINE_TYPES.WEAVING';
      case MachineType.TUFT:
        return 'MACHINE.DETAILS.MACHINE_TYPES.TUFTING';
      case MachineType.WINDER:
        return 'MACHINE.DETAILS.MACHINE_TYPES.WINDER';
      case MachineType.DYEING:
        return 'MACHINE.DETAILS.MACHINE_TYPES.DYEING';
      case MachineType.PLASTIC_MACHINE:
        return 'MACHINE.DETAILS.MACHINE_TYPES.PLASTIC';
    }
  }

  public getMachineTypeDetails(equipmentType: MachineType, machineType: GenericMachineType): MachineTypeDetail[] {
    const basicMachineTypeDetails: MachineTypeDetail[] = [
      {position: 1, label: this.translationKeyMap.get('manufacturer'), value: machineType.manufacturer?.name},
      {position: 2, label: this.translationKeyMap.get('model'), value: machineType.model}
    ];
    const technology: MachineTypeDetail = {
      position: 3,
      label: this.translationKeyMap.get('technology'),
      value: (machineType as WeavingMachineType).technology?.name
        ? this.translateService.instant(`${this.translationKeyMap.get('technologies')}.${(machineType as WeavingMachineType).technology.name}`)
        : null
    };
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return [
          ...basicMachineTypeDetails,
          technology,
          {position: 4, label: this.translationKeyMap.get('width'), value: (machineType as WeavingMachineType).width.toString(10), labelForUnit: this.translationKeyMap.get('centimeter')}
        ];
      case MachineType.TUFT:
        return [
          ...basicMachineTypeDetails,
          {position: 3, label: this.translationKeyMap.get('width'), value: (machineType as TuftingMachineType).width.toString(10), labelForUnit: this.translationKeyMap.get('centimeter')},
          {position: 4, label: this.translationKeyMap.get('gauge'), value: (machineType as TuftingMachineType).gauge.fractionalValueAsString, labelForUnit: this.translationKeyMap.get('inch')}
        ];
      case MachineType.WINDER:
        return basicMachineTypeDetails;
      case MachineType.DYEING:
        return [
          ...basicMachineTypeDetails,
          technology,
          {
            position: 4,
            label: this.translationKeyMap.get('controllerType'),
            value: (machineType as DyeingMachineType).controllerType?.name
          },
          {position: 5, label: this.translationKeyMap.get('minimumLoad'), value: (machineType as DyeingMachineType).minimumLoadInKg?.toString(), labelForUnit: this.translationKeyMap.get('kg')},
          {position: 6, label: this.translationKeyMap.get('maximumLoad'), value: (machineType as DyeingMachineType).maximumLoadInKg?.toString(), labelForUnit: this.translationKeyMap.get('kg')},
          {
            position: 7,
            label: this.translationKeyMap.get('maximumTemperature'),
            value: (machineType as DyeingMachineType).maximumTemperatureInCelsius?.toString(),
            labelForUnit: this.translationKeyMap.get('celsius')
          }
        ];
      case MachineType.PLASTIC_MACHINE:
        return [
          basicMachineTypeDetails[0],
          technology,
          {
            position: 4,
            label: this.translationKeyMap.get('standardSpeed'),
            value: this.translateService.instant(`${this.translationKeyMap.get('standardSpeeds')}.${(machineType as PlasticsMachineType).standardSpeed}`)
          }
        ];
    }
  }

  public getMachineTypeGridIdentifier(equipmentType: MachineType): GridIdentifier {
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return GridIdentifier.SELECT_WEAVING_MACHINE_TYPE;
      case MachineType.TUFT:
        return GridIdentifier.SELECT_TUFT_MACHINE_TYPE;
      case MachineType.WINDER:
        return GridIdentifier.SELECT_WINDER_MACHINE_TYPE;
      case MachineType.DYEING:
        return GridIdentifier.SELECT_DYEING_MACHINE_TYPE;
      case MachineType.PLASTIC_MACHINE:
        return GridIdentifier.SELECT_PLASTICS_MACHINE_TYPE;
    }
  }

  public getUrlToAddPage(equipmentType: MachineType): string {
    switch (equipmentType) {
      case MachineType.WEAVING_MACHINE:
        return RouteUtils.paths.texState.addWeavingMachineType.absolutePath;
      case MachineType.TUFT:
        return RouteUtils.paths.texState.addTuftingMachineType.absolutePath;
      case MachineType.WINDER:
        return RouteUtils.paths.texState.addWinderMachineType.absolutePath;
      case MachineType.DYEING:
        return RouteUtils.paths.texState.addDyeingMachineType.absolutePath;
      case MachineType.PLASTIC_MACHINE:
        return RouteUtils.paths.texState.addPlasticMachineType.absolutePath;
    }
  }

  private getColumnDefsForWeavingMachine(): ColDef[] {
    return [
      ...this.getGenericColumns(),
      this.getTechnologyColDef(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('width')
        .withColId('width')
        .withHeaderName(this.translationKeyMap.get('width'))
        .withHeaderClass('right')
        .withCellClass('right')
        .withTooltipField('width')
        .withValueFormatter((params: ValueFormatterParams) => this.addUnitToString(params.value, this.translationKeyMap.get('centimeter')))
        .build()
    ];
  }

  private getColumnDefsForTuftingMachine(): ColDef[] {
    return [
      ...this.getGenericColumns(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('width')
        .withColId('width')
        .withHeaderName(this.translationKeyMap.get('width'))
        .withHeaderClass('right')
        .withCellClass('right')
        .withTooltipField('width')
        .withValueFormatter((params: ValueFormatterParams) => this.addUnitToString(params.value, this.translationKeyMap.get('centimeter')))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('gauge')
        .withColId('gauge')
        .withHeaderName(this.translationKeyMap.get('gauge'))
        .withHeaderClass('right')
        .withComparator(StringUtils.stringComparator)
        .withTooltipField('gauge.name')
        .withValueGetter((params: ValueGetterParams) => params.data.gauge.name)
        .build()
    ];
  }

  private getColumnDefsForWinderMachine(): ColDef[] {
    return [
      ...this.getGenericColumns(),
      this.colDefBuilderFactoryService.getBuilder().withField('model').withColId('model').withHeaderName(this.translationKeyMap.get('model')).withTooltipField('model').build()
    ];
  }

  private getColumnDefsForDyeingMachine(): ColDef[] {
    return [
      ...this.getGenericColumns(),
      this.getTechnologyColDef(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('controllerType.name')
        .withColId('controllerType')
        .withHeaderName(this.translationKeyMap.get('controllerType'))
        .withComparator(StringUtils.stringComparator)
        .build()
    ];
  }

  private getColumnDefsForPlasticsMachine(): ColDef[] {
    return [...this.getGenericColumns(), this.getTechnologyColDef()];
  }

  private getGenericColumns(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withField('name').withColId('name').withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator).withTooltipField('name').build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('manufacturer.name')
        .withColId('manufacturer')
        .withHeaderName(this.translationKeyMap.get('manufacturer'))
        .withComparator(StringUtils.stringComparator)
        .withTooltipField('manufacturer.name')
        .build()
    ];
  }

  private getTechnologyColDef(): ColDef {
    return this.colDefBuilderFactoryService
      .getBuilder()
      .withField('technology.name')
      .withColId('technology')
      .withHeaderName(this.translationKeyMap.get('technology'))
      .withComparator(StringUtils.stringComparator)
      .withValueFormatter((params: ValueFormatterParams) => (params.value ? this.translateService.instant(`${this.translationKeyMap.get('technologies')}.${params.value}`) : null))
      .build();
  }

  private addUnitToString(value: string | number, unitTranslationKey: string): string {
    return value + ' ' + this.translateService.instant(unitTranslationKey);
  }
}
